/**
 * screens/hostConsole.tsx — Host Console (tablet-first), the core operational
 * screen. Stats strip + Call next + Add walk-in; a FIFO live queue (per-row
 * Auto-seat / Pick / Seat / No-show) on the left; the live floor map on the
 * right (Free / Occupied / Held, tap-to-seat the picked party, tap-occupied to
 * settle). Faithful port of the prototype's Host surface + _hostVM.
 */

import React, { useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useQueue } from '../queue/store';
import { Switch, Modal } from '../components/ui';
import { waitingFIFO, positionOf } from '../queue/engine';
import { fmtWait, waitMin, fmtPhone, initials } from '../queue/format';
import { Mono } from '../queue/components/atoms';
import { CHANNEL_LABEL } from '../queue/stateStyle';
import { WalkinModal, SettleModal, NoShowModal, EntryModal } from '../queue/components/modals';
import { createResource, deleteResource } from '../services/queue/queueService';
import { handleApiError } from '../utils/errorHandler';
import { showToast } from '../components/toastComponent';
import type { Entry, Resource } from '../queue/types';
import { CONTENT, useResponsive } from '../lib/responsive';

type ModalKind = null | 'walkin' | 'settle' | 'noshow' | 'entry';

export default function HostConsoleScreen() {
  const { theme } = useTheme();
  const { colors } = theme;
  // Seating actions are mode-aware in the store: live mode (FITTED) routes them
  // to sk-queue-api → SK Calendar via the backbone; memory mode runs the engine.
  // The screen + modals are identical either way.
  const { state, selectedEntryId, selectEntry, clearSelection, autoSeat, seatCalled, callNext, pickResource, liveQueueId, refetchLive } = useQueue();
  const { width } = useResponsive();

  // Line paused = "Accepting walk-ins" OFF. In live mode state.cfg2 is synced
  // from the queue on refetch (store.refetchLive → dtoToCfg2), so this reflects
  // the real backend flag. Gates the "Add walk-in" button (see below).
  const paused = !state.cfg2.accepting;

  // Venue-wide "Allow communal seating" master switch (Settings). A table's
  // per-table `shared` flag only takes effect when this is on (seating gates on
  // r.shared && cfg2.shared_seating). Used to disable the per-table toggle in
  // the Add-table modal and to grey a SHARED badge that's currently inactive.
  const communalAllowed = state.cfg2.shared_seating;

  // Below this the queue + floor stack vertically and the surface page-scrolls.
  // Above it, the two panels sit side-by-side and scroll internally (chrome
  // fixed — tablet-app behaviour). Threshold is 1100 (not 900) because the
  // sidebar eats 256px: a side-by-side split needs ≥~840px of content width or
  // the dense queue rows crush (phone text wrapping one char per line). So a
  // portrait/26"-class tablet stacks; a landscape tablet / laptop goes 2-up.
  const narrow = width < 1100;

  const [modal, setModal] = useState<ModalKind>(null);
  const [settleRes, setSettleRes] = useState<string | null>(null);
  const [targetEntry, setTargetEntry] = useState<string | null>(null);

  // Add-table form (live mode only — tables are real cal calendars).
  const [addOpen, setAddOpen] = useState(false);
  const [tName, setTName] = useState('');
  const [tSeats, setTSeats] = useState('4');
  const [tZone, setTZone] = useState('');
  const [tShared, setTShared] = useState(false);
  const [tBusy, setTBusy] = useState(false);
  const canManageFloor = !!liveQueueId; // real tables exist only in live mode

  const submitAddTable = async () => {
    if (!liveQueueId) return;
    const label = tName.trim();
    const capacity = Math.max(1, parseInt(tSeats || '0', 10));
    if (!label) { showToast('error', 'Name required', 'Give the table a name (e.g. T4, Bar).'); return; }
    setTBusy(true);
    try {
      await createResource(liveQueueId, { label, capacity, zone: tZone.trim() || undefined, shared: tShared });
      await refetchLive();
      showToast('success', 'Table added', `${label} · seats ${capacity}${tShared ? ' · shared' : ''}`);
      setAddOpen(false); setTName(''); setTSeats('4'); setTZone(''); setTShared(false);
    } catch (err) {
      handleApiError(err, 'Could not add table');
    } finally {
      setTBusy(false);
    }
  };

  const removeTable = async (ref: string, label: string) => {
    if (!liveQueueId) return;
    try {
      await deleteResource(liveQueueId, ref);
      await refetchLive();
      showToast('success', 'Table removed', label);
    } catch (err) {
      handleApiError(err, 'Could not remove table');
    }
  };

  const waiting = waitingFIFO(state.entries);
  const called = state.entries.filter(e => e.state === 'CALLED').sort((a, b) => a.joinAt - b.joinAt);
  const seatedCount = state.entries.filter(e => e.state === 'SEATED').length;
  const freeCount = state.resources.filter(r => r.status === 'free').length;
  const selEntry = selectedEntryId ? state.entries.find(e => e.id === selectedEntryId) : null;

  // allocatedResource is a resource REF — in live mode that's the cal calendar
  // ULID, not a friendly name. Resolve it to the table's label ("T2") for
  // display. (In the in-memory demo id===label, so this is a no-op there.)
  const tableLabel = (ref: string | null) =>
    (ref && state.resources.find(r => r.id === ref)?.label) || ref || '';

  const stats = [
    { k: 'Waiting', v: String(waiting.length) },
    { k: 'Called', v: String(called.length) },
    { k: 'Seated', v: String(seatedCount) },
    { k: 'Free tables', v: String(freeCount) },
  ];

  function openEntry(id: string) { setTargetEntry(id); setModal('entry'); }
  function openNoShow(id: string) { setTargetEntry(id); setModal('noshow'); }

  // Tapping a floor cell: occupied → settle; otherwise the store decides
  // (pick the selected party, or prompt).
  function onCell(r: Resource) {
    if (r.status === 'occupied' && !selectedEntryId) { setSettleRes(r.id); setModal('settle'); return; }
    pickResource(r.id);
  }

  const rows: Array<{ e: Entry; isCalled: boolean }> = [
    ...called.map(e => ({ e, isCalled: true })),
    ...waiting.map(e => ({ e, isCalled: false })),
  ];

  // ── Section renderers ──────────────────────────────────────────────────
  // Defined once, then composed two ways: on wide screens the queue/floor
  // scroll internally (chrome fixed); on phone the whole card scrolls as one.

  const topBar = (
            <View style={{
              flexDirection: 'row', alignItems: 'center', gap: 16, paddingHorizontal: 20, paddingVertical: 14,
              backgroundColor: colors.card, borderBottomWidth: 1, borderBottomColor: colors.line, flexWrap: 'wrap',
            }}>
              <View>
                <Text style={{ fontSize: 15, fontWeight: '700', color: colors.ink }}>Host Console</Text>
                <Mono style={{ fontSize: 11, color: colors.ink4 }}>FITTED · staff seating · live</Mono>
              </View>
              <View style={{ flexDirection: 'row', gap: 8, marginLeft: 6 }}>
                {stats.map(s => (
                  <View key={s.k} style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 9, paddingHorizontal: 12, paddingVertical: 6, backgroundColor: colors.paper, minWidth: 64 }}>
                    <Mono style={{ fontSize: 19, fontWeight: '600', lineHeight: 20, color: colors.ink }}>{s.v}</Mono>
                    <Text style={{ fontSize: 10, color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>{s.k}</Text>
                  </View>
                ))}
              </View>
              <View style={{ marginLeft: 'auto', flexDirection: 'row', alignItems: 'center', gap: 9 }}>
                {/* When the line is paused (Accepting walk-ins OFF), the backend
                    rejects EVERY new entry (ERR_NOT_ACCEPTING) — host adds too,
                    not just guest self-join. So disable "Add walk-in" instead of
                    letting the operator fill the modal only to fail on submit. */}
                {paused && (
                  <Mono style={{ fontSize: 10, letterSpacing: 0.5, color: colors.ink4, textTransform: 'uppercase' }}>⏸ Line paused</Mono>
                )}
                <TouchableOpacity onPress={callNext} style={{ height: 38, paddingHorizontal: 16, borderRadius: 9, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.card, justifyContent: 'center' }}>
                  <Text style={{ fontWeight: '500', fontSize: 13.5, color: colors.ink }}>Call next ▸</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  disabled={paused}
                  onPress={() => setModal('walkin')}
                  accessibilityState={{ disabled: paused }}
                  accessibilityLabel={paused ? 'Add walk-in (disabled — line is paused)' : 'Add walk-in'}
                  style={{ height: 38, paddingHorizontal: 18, borderRadius: 9, backgroundColor: colors.ink, justifyContent: 'center', opacity: paused ? 0.4 : 1 }}
                >
                  <Text style={{ fontWeight: '600', fontSize: 13.5, color: colors.white }}>+ Add walk-in</Text>
                </TouchableOpacity>
              </View>
            </View>
  );

  // Live-queue sub-header (fixed) + its rows (the scrolling part).
  const queueHeader = (
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.paper, borderBottomWidth: 1, borderBottomColor: colors.line, paddingHorizontal: 16, paddingVertical: 11 }}>
                  <Text style={{ fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink3 }}>Live queue</Text>
                  <Mono style={{ fontSize: 11, color: colors.ink4 }}>FIFO · auto / pick / party-size</Mono>
                </View>
  );

  const queueRows = (
                <>

                {selEntry && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 16, paddingVertical: 10, backgroundColor: colors.ink }}>
                    <Text style={{ fontSize: 12.5, color: colors.white, flex: 1 }}>Seating {selEntry.displayName} · party of {selEntry.party} — tap a highlighted table, or</Text>
                    <TouchableOpacity onPress={() => autoSeat(selEntry.id)} style={{ height: 26, paddingHorizontal: 11, borderRadius: 6, borderWidth: 1, borderColor: colors.white, justifyContent: 'center' }}>
                      <Text style={{ fontSize: 12, color: colors.white }}>auto-seat</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={clearSelection}>
                      <Text style={{ fontSize: 12, color: colors.ink4 }}>cancel</Text>
                    </TouchableOpacity>
                  </View>
                )}

                {rows.length === 0 ? (
                  <View style={{ paddingVertical: 60, alignItems: 'center' }}>
                    <Text style={{ fontSize: 30, marginBottom: 10, color: colors.ink4 }}>✓</Text>
                    <Text style={{ fontSize: 14, color: colors.ink4 }}>The line is empty — every party is seated.</Text>
                  </View>
                ) : rows.map(({ e, isCalled }) => {
                  const pos = positionOf(state.entries, e.id);
                  const selected = e.id === selectedEntryId;
                  const waited = Math.max(0, Math.round((state.now - e.joinAt) / 60000));
                  return (
                    <TouchableOpacity key={e.id} activeOpacity={0.7} onPress={() => openEntry(e.id)} style={{
                      flexDirection: 'row', alignItems: 'center', gap: narrow ? 10 : 13,
                      paddingHorizontal: 16, paddingVertical: 13,
                      // On phone the row wraps so the action buttons drop to a
                      // full-width second line instead of squeezing the name out.
                      flexWrap: narrow ? 'wrap' : 'nowrap',
                      borderBottomWidth: 1, borderBottomColor: colors.paper3,
                      backgroundColor: selected ? colors.paper2 : (isCalled ? '#FBFAF6' : colors.card),
                      ...(selected ? { borderLeftWidth: 3, borderLeftColor: colors.ink } : {}),
                    }}>
                      <Mono style={{ fontSize: 12, fontWeight: '600', color: isCalled ? colors.ink : colors.ink3, width: 26 }}>#{e.token}</Mono>
                      <View style={{ width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: isCalled ? colors.ink : colors.paper3 }}>
                        <Text style={{ fontSize: 13, fontWeight: '600', color: isCalled ? colors.white : colors.ink2 }}>{initials(e.displayName)}</Text>
                      </View>
                      <View style={{ flex: 1, minWidth: narrow ? 120 : 0 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <Text style={{ fontSize: 14, fontWeight: '600', color: colors.ink, flexShrink: 1 }} numberOfLines={1}>{e.displayName}</Text>
                          <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: 4, paddingHorizontal: 5, paddingVertical: 1 }}>
                            <Mono style={{ fontSize: 10, color: colors.ink4 }}>{CHANNEL_LABEL[e.channel] || e.channel}</Mono>
                          </View>
                        </View>
                        <Text style={{ fontSize: 11.5, color: colors.ink4, marginTop: 2 }}>
                          +91 {fmtPhone(e.phone)} · {isCalled ? (tableLabel(e.allocatedResource) || '—') + ' held' : 'waited ' + waited + 'm · ' + fmtWait(waitMin(pos ?? 0))}
                        </Text>
                      </View>
                      <View style={{ alignItems: 'center', width: 54 }}>
                        <Mono style={{ fontSize: 17, fontWeight: '600', lineHeight: 18, color: colors.ink }}>{e.party}</Mono>
                        <Text style={{ fontSize: 9, color: colors.ink4, letterSpacing: 0.4 }}>PARTY</Text>
                      </View>
                      <Mono style={{ fontSize: 12, fontWeight: '600', color: colors.ink3, width: 42, textAlign: 'center' }}>{isCalled ? 'READY' : '#' + pos}</Mono>
                      <View style={{ flexDirection: 'row', gap: 7, ...(narrow ? { flexBasis: '100%', marginTop: 4, justifyContent: 'flex-end' } : {}) }}>
                        {isCalled ? (
                          <>
                            <TouchableOpacity onPress={() => seatCalled(e.id)} style={actBtn(colors, true)}>
                              <Text style={actText(colors, true)}>Seat → {tableLabel(e.allocatedResource)}</Text>
                            </TouchableOpacity>
                            <TouchableOpacity onPress={() => openNoShow(e.id)} style={actBtn(colors, false)}>
                              <Text style={actText(colors, false)}>No-show</Text>
                            </TouchableOpacity>
                          </>
                        ) : (
                          <>
                            <TouchableOpacity onPress={() => autoSeat(e.id)} style={actBtn(colors, true)}>
                              <Text style={actText(colors, true)}>Auto-seat</Text>
                            </TouchableOpacity>
                            <TouchableOpacity onPress={() => selectEntry(e.id)} style={{
                              height: 30, paddingHorizontal: 12, borderRadius: 7, justifyContent: 'center', borderWidth: 1,
                              backgroundColor: selected ? colors.ink : colors.card, borderColor: selected ? colors.ink : colors.lineStrong,
                            }}>
                              <Text style={{ fontSize: 12.5, fontWeight: '500', color: selected ? colors.white : colors.ink }}>Pick</Text>
                            </TouchableOpacity>
                          </>
                        )}
                      </View>
                    </TouchableOpacity>
                  );
                })}
                </>
  );

  // Floor sub-header (fixed) + the table grid + legend (the scrolling part).
  const floorHeader = (
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.paper, borderBottomWidth: 1, borderBottomColor: colors.line, paddingHorizontal: 16, paddingVertical: 11 }}>
                  <Text style={{ fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink3 }}>Floor</Text>
                  {canManageFloor ? (
                    <TouchableOpacity onPress={() => setAddOpen(true)} style={{ height: 28, paddingHorizontal: 11, borderRadius: 7, borderWidth: 1, borderColor: colors.lineStrong, justifyContent: 'center', backgroundColor: colors.card }}>
                      <Text style={{ fontSize: 12, fontWeight: '600', color: colors.ink }}>+ Add table</Text>
                    </TouchableOpacity>
                  ) : (
                    <Mono style={{ fontSize: 11, color: colors.ink4 }}>colour = live truth</Mono>
                  )}
                </View>
  );

  const floorContent = (
                <View style={{ padding: 16 }}>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 11 }}>
                    {state.resources.map(r => {
                      const occ = r.occupant ? state.entries.find(e => e.id === r.occupant) : null;
                      const held = r.heldFor ? state.entries.find(e => e.id === r.heldFor) : null;
                      // A shared/communal table keeps taking parties until its seats
                      // run out, so "fits" includes a partly-filled shared table with
                      // room (remaining seats >= party), not just a free one.
                      const sharedOn = r.shared && state.cfg2.shared_seating;
                      const remaining = r.cap - (r.seatsTaken ?? 0);
                      const fits = !!selEntry && (
                        (r.status === 'free' && (r.cap >= selEntry.party || sharedOn)) ||
                        (sharedOn && r.status === 'occupied' && remaining >= selEntry.party)
                      );
                      let bg = colors.card, border = colors.lineStrong, labelColor = colors.ink, subColor = colors.ink3, sub = 'Seats ' + r.cap, dashed = false, bw = 1;
                      if (r.status === 'free') {
                        border = fits ? colors.ink : '#DCDCD8'; bw = fits ? 2 : 1;
                        if (fits) sub = 'Tap to seat';
                      } else if (r.status === 'occupied') {
                        bg = colors.ink; border = fits ? colors.ink2 : colors.ink; labelColor = colors.white; subColor = colors.ink4; bw = fits ? 2 : 1;
                        // Shared: show seat fill (e.g. "5/8 · 2 parties") + tap-to-add hint.
                        sub = sharedOn
                          ? `${r.seatsTaken ?? 0}/${r.cap} seats${(r.partyCount ?? 0) > 1 ? ` · ${r.partyCount} parties` : ''}${fits ? ' · tap to add' : ''}`
                          : (occ ? occ.displayName : 'Occupied') + ' · ' + (occ ? occ.party : '');
                      } else { // held
                        bg = colors.paper2; border = colors.ink; dashed = true; bw = 2; sub = 'Held → ' + (held ? held.displayName : '');
                      }
                      return (
                        <TouchableOpacity key={r.id} onPress={() => onCell(r)} style={{
                          width: '31%', minHeight: 78, borderRadius: 12, padding: 13, justifyContent: 'space-between',
                          backgroundColor: bg, borderWidth: bw, borderColor: border, ...(dashed ? { borderStyle: 'dashed' } : {}),
                        }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                              <Mono style={{ fontWeight: '600', fontSize: 15, color: labelColor }}>{r.label}</Mono>
                              {r.shared && (
                                // When the venue-wide master is off, this table's
                                // shared flag is dormant — show "SHARED · OFF" and
                                // dim it so the badge never implies active communal
                                // seating that isn't happening.
                                <Text style={{ fontSize: 8, fontWeight: '700', letterSpacing: 0.5, color: labelColor, opacity: communalAllowed ? 0.55 : 0.3, borderWidth: 1, borderColor: labelColor, borderRadius: 3, paddingHorizontal: 3, paddingVertical: 1 }}>
                                  {communalAllowed ? 'SHARED' : 'SHARED · OFF'}
                                </Text>
                              )}
                            </View>
                            <Text style={{ fontSize: 9, fontWeight: '600', letterSpacing: 0.5, color: labelColor, opacity: 0.5 }}>{r.cap}p</Text>
                          </View>
                          <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                            <Text style={{ fontSize: 11, color: subColor, marginTop: 6, lineHeight: 14, flex: 1 }}>{sub}</Text>
                            {canManageFloor && r.status === 'free' && (
                              <TouchableOpacity
                                onPress={() => removeTable(r.id, r.label)}
                                accessibilityLabel={`Remove ${r.label}`}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                                style={{ paddingHorizontal: 4 }}
                              >
                                <Text style={{ fontSize: 13, color: colors.ink4 }}>✕</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        </TouchableOpacity>
                      );
                    })}
                  </View>

                  {/* legend */}
                  <View style={{ marginTop: 18, borderTopWidth: 1, borderTopColor: colors.line, paddingTop: 14, gap: 9 }}>
                    <Text style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink4 }}>Legend</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
                      <Legend colors={colors} sw={{ backgroundColor: colors.card, borderWidth: 1, borderColor: '#DCDCD8' }} label="Free" />
                      <Legend colors={colors} sw={{ backgroundColor: colors.ink }} label="Occupied · tap to settle" />
                      <Legend colors={colors} sw={{ backgroundColor: colors.paper2, borderWidth: 2, borderColor: colors.ink, borderStyle: 'dashed' }} label="Held" />
                    </View>
                    <Text style={{ fontSize: 11.5, color: colors.ink4, lineHeight: 17, marginTop: 2 }}>
                      Tables free automatically when Billing settles the order — no manual tap needed. Tap an occupied table to simulate the settle event.
                    </Text>
                  </View>
                </View>
  );

  const modals = (
    <>
      <WalkinModal open={modal === 'walkin'} onClose={() => setModal(null)} />
      <SettleModal open={modal === 'settle'} onClose={() => setModal(null)} resId={settleRes} />
      <NoShowModal open={modal === 'noshow'} onClose={() => setModal(null)} entryId={targetEntry} />
      <EntryModal open={modal === 'entry'} onClose={() => setModal(null)} entryId={targetEntry} onNoShow={openNoShow} />
      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add table"
        size="sm"
        footer={
          <>
            <TouchableOpacity onPress={() => setAddOpen(false)} style={{ height: 40, paddingHorizontal: 16, borderRadius: 9, borderWidth: 1, borderColor: colors.lineStrong, justifyContent: 'center' }}>
              <Text style={{ fontSize: 13, color: colors.ink3 }}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity disabled={tBusy} onPress={submitAddTable} style={{ height: 40, paddingHorizontal: 16, borderRadius: 9, backgroundColor: colors.ink, justifyContent: 'center', opacity: tBusy ? 0.6 : 1 }}>
              <Text style={{ fontSize: 13, fontWeight: '600', color: colors.white }}>{tBusy ? 'Adding…' : 'Add table'}</Text>
            </TouchableOpacity>
          </>
        }
      >
        <View style={{ gap: 14 }}>
          <View>
            <Text style={{ fontSize: 12.5, fontWeight: '600', color: colors.ink, marginBottom: 6 }}>Name</Text>
            <TextInput value={tName} onChangeText={setTName} placeholder="T4, Bar, Patio-1…" placeholderTextColor={colors.ink4}
              style={{ height: 40, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 9, paddingHorizontal: 12, fontSize: 14, color: colors.ink } as any} />
          </View>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 12.5, fontWeight: '600', color: colors.ink, marginBottom: 6 }}>Seats</Text>
              <TextInput value={tSeats} onChangeText={(v) => setTSeats(v.replace(/\D/g, ''))} keyboardType="number-pad"
                style={{ height: 40, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 9, paddingHorizontal: 12, fontFamily: 'IBM Plex Mono', fontSize: 14, color: colors.ink } as any} />
            </View>
            <View style={{ flex: 2 }}>
              <Text style={{ fontSize: 12.5, fontWeight: '600', color: colors.ink, marginBottom: 6 }}>Zone (optional)</Text>
              <TextInput value={tZone} onChangeText={setTZone} placeholder="Main, Bar, Patio…" placeholderTextColor={colors.ink4}
                style={{ height: 40, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 9, paddingHorizontal: 12, fontSize: 14, color: colors.ink } as any} />
            </View>
          </View>
          {/* Per-table shared flag. It only DOES anything when the venue-wide
              "Allow communal seating" master switch (Settings) is on — seating
              gates on r.shared && cfg2.shared_seating. So when the master is off
              we disable this toggle and say why, instead of letting the host set
              a flag that silently does nothing. */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, paddingTop: 4, opacity: communalAllowed ? 1 : 0.55 }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>Shared / communal</Text>
              <Text style={{ fontSize: 12, color: colors.ink4 }}>
                {communalAllowed
                  ? 'Strangers share this table (bar, long table) until seats run out.'
                  : 'Turn on “Allow communal seating” in Settings to use shared tables.'}
              </Text>
            </View>
            <Switch checked={communalAllowed && tShared} onChange={setTShared} disabled={!communalAllowed} />
          </View>
        </View>
      </Modal>
    </>
  );

  const card = {
    width: '100%' as const, backgroundColor: colors.card, borderRadius: 14,
    borderWidth: 1, borderColor: colors.line, overflow: 'hidden' as const,
  };

  // ── Phone: the whole card scrolls as one page ─────────────────────────────
  if (narrow) {
    return (
      <ScrollView
        style={{ flex: 1, backgroundColor: colors.paper }}
        contentContainerStyle={{ flexGrow: 1, padding: 12, paddingBottom: 40, width: '100%', maxWidth: CONTENT.wide, alignSelf: 'center' }}
      >
        <View style={card}>
          {topBar}
          <View style={{ borderBottomWidth: 1, borderBottomColor: colors.line }}>
            {queueHeader}
            {queueRows}
          </View>
          <View>
            {floorHeader}
            {floorContent}
          </View>
        </View>
        {modals}
      </ScrollView>
    );
  }

  // ── Wide (tablet → monitor): card fills the viewport; queue + floor scroll
  //    internally so the topbar/headers never move. No pixel-height math — the
  //    shell gives us a bounded flex parent and flex:1 does the rest. ────────
  return (
    <View style={{ flex: 1, backgroundColor: colors.paper }}>
      <View style={{ flex: 1, width: '100%', maxWidth: CONTENT.wide, alignSelf: 'center', padding: 28 }}>
        <View style={[card, { flex: 1, minHeight: 0 }]}>
          {topBar}
          <View style={{ flex: 1, flexDirection: 'row', minHeight: 0 }}>
            {/* live queue — header fixed, rows scroll */}
            <View style={{ flex: 1.45, minHeight: 0, borderRightWidth: 1, borderRightColor: colors.line }}>
              {queueHeader}
              <ScrollView style={{ flex: 1 }}>{queueRows}</ScrollView>
            </View>
            {/* floor — header fixed, grid scrolls */}
            <View style={{ flex: 1, minHeight: 0, backgroundColor: colors.paper }}>
              {floorHeader}
              <ScrollView style={{ flex: 1 }}>{floorContent}</ScrollView>
            </View>
          </View>
        </View>
      </View>
      {modals}
    </View>
  );
}

function Legend({ colors, sw, label }: { colors: any; sw: any; label: string }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
      <View style={{ width: 18, height: 14, borderRadius: 4, ...sw }} />
      <Text style={{ fontSize: 12, color: colors.ink2 }}>{label}</Text>
    </View>
  );
}
function actBtn(colors: any, dark: boolean) {
  return {
    height: 30, paddingHorizontal: 13, borderRadius: 7, justifyContent: 'center' as const, borderWidth: 1,
    backgroundColor: dark ? colors.ink : colors.card, borderColor: dark ? colors.ink : colors.lineStrong,
  };
}
function actText(colors: any, dark: boolean) {
  return { fontSize: 12.5, fontWeight: dark ? '600' as const : '400' as const, color: dark ? colors.white : colors.ink3 };
}
